/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */


  import { lazy, Suspense } from 'react'
  import { Routes, Route, Navigate } from 'react-router-dom'
  import { useAuth } from './context/AuthContext'
  import Layout from './components/Layout'
  import BuildInfo from './components/BuildInfo'
  import Dashboard from './pages/Dashboard'
  import PurchaseOrders from './pages/PurchaseOrders'
  import PODetail from './pages/PODetail'

  // Secondary routes — lazy-loaded for bundle splitting
  const CSVUpload = lazy(() => import('./pages/CSVUpload'))
  const ManualPO = lazy(() => import('./pages/ManualPO'))
  const WindowPO = lazy(() => import('./pages/WindowPO'))
  const SupplierMapping = lazy(() => import('./pages/SupplierMapping'))
  const Items = lazy(() => import('./pages/Items'))

  /**
   * Protected Route - redirects to Portal if not authenticated
   * NO local login - all auth goes through Portal
   */
  function ProtectedRoute({ children }) {
    const { isAuthenticated, loading, redirectToPortalLogin } = useAuth()

    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div className="spinner" style={{ width: 40, height: 40 }}></div>
        </div>
      )
    }

    if (!isAuthenticated) {
      // Redirect to Portal login - NOT a local login page
      redirectToPortalLogin()
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
          <div className="spinner" style={{ width: 40, height: 40 }}></div>
          <p style={{ marginTop: 20, color: '#666' }}>Redirecting to Portal login...</p>
        </div>
      )
    }

    return children
  }

  function RouteFallback() {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>Loading...</div>
      </div>
    )
  }

  function App() {
    return (
      <>
      <BuildInfo />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* All routes require authentication via Portal */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="csv-upload" element={<CSVUpload />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="purchase-orders/:id" element={<PODetail />} />
          <Route path="manual-po" element={<ManualPO />} />
          <Route path="manual-po/:id" element={<ManualPO />} />
          <Route path="window-po" element={<WindowPO />} />
          <Route path="suppliers" element={<SupplierMapping />} />
          <Route path="items" element={<Items />} />
        </Route>

        {/* Any unknown route goes to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </>
    )
  }

  export default App